import type { Theme, FontPairing } from "@structurify/shared-types";
import { MonoLabel } from "../components/primitives/index.ts";

interface Props {
  theme: Theme;
  fonts: FontPairing;
  progress: number;
  statusLine: string;
  fileName?: string | undefined;
  fileSize?: number | undefined;
  mimeType?: string | undefined;
  objectUrl?: string | null | undefined;
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function StageAnalyze({ theme, fonts, progress, statusLine, fileName, fileSize, mimeType, objectUrl }: Props) {
  const isImage = mimeType?.startsWith("image/") ?? false;
  const isPdf = mimeType === "application/pdf";
  const hasPreview = !!objectUrl && (isImage || isPdf);

  return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
      <div style={{ position: "relative", width: 380, height: 492 }}>
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: theme.docPaper,
            border: `1px solid ${theme.rule}`,
            boxShadow: "0 1px 0 rgba(0,0,0,0.04), 0 18px 48px rgba(20,16,10,0.10)",
            borderRadius: 2,
            animation: "paperSlide 600ms ease-out",
            overflow: "hidden",
          }}
        >
          {hasPreview ? (
            isImage ? (
              <img src={objectUrl!} alt="Document preview" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
            ) : (
              <iframe src={objectUrl!} title="Document preview" style={{ width: "100%", height: "100%", border: "none", display: "block" }} />
            )
          ) : (
          <div style={{ padding: "34px 28px" }}>
            {Array.from({ length: 14 }).map((_, i) => {
              const isHeader = i === 0 || i === 5 || i === 10;
              const widths = ["82%", "64%", "78%", "40%", "90%", "55%", "70%", "60%", "85%", "50%", "75%", "30%", "80%", "62%"];
              return (
                <div
                  key={i}
                  style={{
                    height: isHeader ? 10 : 6,
                    width: isHeader ? "50%" : (widths[i] ?? "60%"),
                    background: isHeader ? theme.ink : theme.inkMute,
                    opacity: isHeader ? 0.85 : 0.2,
                    marginBottom: isHeader ? 16 : 11,
                    borderRadius: 1,
                  }}
                />
              );
            })}
          </div>
          )}

          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              height: 84,
              top: -84,
              background: `linear-gradient(180deg, transparent 0%, ${theme.accentSoft} 50%, transparent 100%)`,
              animation: "scan 2400ms cubic-bezier(.5,.05,.5,.95) infinite",
              mixBlendMode: "multiply",
            }}
          />
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              height: 1,
              top: 0,
              background: theme.accent,
              opacity: 0.5,
              animation: "scanLine 2400ms cubic-bezier(.5,.05,.5,.95) infinite",
            }}
          />
        </div>
      </div>

      <div style={{ position: "absolute", bottom: 56, left: 0, right: 0, textAlign: "center" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 14 }}>
          <MonoLabel theme={theme} fonts={fonts} size={10} color={theme.inkSoft}>
            {statusLine}
          </MonoLabel>
          <div style={{ width: 240, height: 1, background: theme.rule, position: "relative", overflow: "hidden" }}>
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: `${progress * 100}%`,
                background: theme.ink,
                transition: "width 400ms ease",
              }}
            />
          </div>
          <MonoLabel theme={theme} fonts={fonts} size={10} color={theme.inkMute}>
            {String(Math.round(progress * 100)).padStart(2, "0")}%
          </MonoLabel>
        </div>
        <div style={{ fontFamily: fonts.sans, fontSize: 13, color: theme.inkSoft, marginTop: 18, fontWeight: 400, letterSpacing: "-0.005em" }}>
          {fileName ?? "document"}{fileSize != null ? ` · ${formatSize(fileSize)}` : ""}
        </div>
      </div>
    </div>
  );
}
